/**
 * Zod schemas for the backend wire shapes.
 *
 * Each resource file imports the schema it needs and passes it to the api
 * client; the client does the parse at the boundary. The runtime shape and
 * the TS type are kept in lockstep via `z.infer`.
 *
 * NOTE: these schemas are deliberately lenient — they accept ANY string for
 * timestamps and IDs (the brand layer in `@/types/money` is *not* enforced
 * at parse time, because branding is a compile-time concern). Sub-field
 * narrowing (e.g. valid IANA timezone, valid ISO datetime) happens in the
 * domain layer, not at the wire boundary.
 */

import { z } from 'zod';

/* ============================================================================
 * Common envelopes
 * ============================================================================ */

export const apiErrorBodySchema = z.object({ detail: z.string() });
export const apiValidationErrorBodySchema = z.object({
  detail: z.array(
    z.object({
      loc: z.array(z.union([z.string(), z.number()])),
      msg: z.string(),
      type: z.string(),
      input: z.unknown().optional(),
      ctx: z.record(z.unknown()).optional(),
    }),
  ),
});

export const healthSchema = z.object({ status: z.string() });

/* ============================================================================
 * Users
 * ============================================================================ */

export const userStatusSchema = z.enum([
  'pending_email_verification',
  'active',
  'suspended',
  'deleted',
]);

export const userSchema = z.object({
  id: z.string(),
  email: z.string().email().or(z.string()),
  display_name: z.string().nullable(),
  status: userStatusSchema,
  created_at: z.string(),
});

export const createUserResponseSchema = userSchema.extend({
  api_key: z.string(),
});

/* ============================================================================
 * Broker accounts
 * ============================================================================ */

export const brokerAccountSchema = z.object({
  id: z.string(),
  client_id: z.string(),
  broker_type: z.string(),
  display_name: z.string().nullable(),
  position_mode: z.enum(['netting', 'hedging']),
  base_currency: z.string(),
  timezone: z.string(),
  status: z.enum(['active', 'paused']),
  enabled: z.boolean().optional(),
  last_authenticated_at: z.string().nullable().optional(),
  last_auth_error: z.string().nullable().optional(),
  created_at: z.string(),
  // ─── Admin (fleet view) extensions — only populated by /admin/* endpoints ───
  // Renamed from client_email/client_display_name in migration #10 to track the
  // backend's users→clients rename.
  client_email: z.string().nullable().optional(),
  client_display_name: z.string().nullable().optional(),
});
export const brokerAccountListSchema = z.array(brokerAccountSchema);

/* ============================================================================
 * Subscriptions
 * ============================================================================ */

export const sizingConfigWireSchema = z.object({
  lots_multiplier: z.number(),
  fixed_qty_multiplier: z.number(),
  capital: z.number(),
  max_qty: z.number(),
});

export const subscriptionSchema = z.object({
  id: z.string(),
  client_id: z.string(),
  broker_account_id: z.string(),
  strategy_id: z.string(),
  subscribed_at: z.string(),
  sizing_config: sizingConfigWireSchema,
  active: z.boolean(),
  paused_reason: z.string().nullable(),
});
export const subscriptionListSchema = z.array(subscriptionSchema);

/** Admin fleet-view variant — strict superset with denormalised fields. */
export const adminSubscriptionSchema = subscriptionSchema.extend({
  client_email: z.string().nullable(),
  client_display_name: z.string().nullable(),
  broker_type: z.string().nullable(),
  broker_display_name: z.string().nullable(),
  strategy_code_name: z.string().nullable(),
  strategy_status: z.string().nullable(),
  // Tolerant: an older backend (pre-restart) omits `mode` entirely — degrade to
  // undefined (treated as non-live → inert ARM cell) instead of crashing the list.
  mode: z.enum(['paper', 'live']).nullish(),   // gates the ARM control
});
export const adminSubscriptionListSchema = z.array(adminSubscriptionSchema);

/* ============================================================================
 * Strategies
 * ============================================================================ */

// Strategy: status is a free-form string (backend may evolve the values;
// frontend should not break when a new state is added) — extensibility.
// Optional denormalized counts come from /admin/strategies; older shapes
// without them still parse.
export const strategySchema = z.object({
  id: z.string(),
  code_name: z.string(),
  version: z.string(),
  params: z.record(z.unknown()),
  status: z.string(),
  subscription_count: z.number().int().optional(),
  active_subscription_count: z.number().int().optional(),
  signal_count: z.number().int().optional(),
  deployment_count: z.number().int().optional(),
  last_signal_at: z.string().nullable().optional(),
  deployed_at: z.string().nullable().optional(),
});
export const strategyListSchema = z.array(strategySchema);

/* ============================================================================
 * Trades, P&L, Signals, Risk, Recon
 * ============================================================================ */

export const tradeSchema = z.object({
  id: z.string(),
  symbol: z.string(),
  side: z.enum(['buy', 'sell']),
  quantity: z.number(),
  entry_time: z.string(),
  entry_price: z.number(),
  exit_time: z.string().nullable(),
  exit_price: z.number().nullable(),
  is_open: z.boolean(),
  realized_pnl: z.number(),
  entry_signal: z.string(),
  exit_signal: z.string(),
});
export const tradeListSchema = z.array(tradeSchema);

export const pnlSnapshotSchema = z.object({
  time: z.string(),
  realized: z.number(),
  unrealized: z.number(),
  total: z.number(),
});
export const pnlSnapshotListSchema = z.array(pnlSnapshotSchema);

export const signalLegSchema = z.object({
  symbol: z.string(),
  side: z.enum(['buy', 'sell']),
  size_unit: z.number(),
  sizing_mode: z.enum(['lots', 'fixed_qty', 'percent']),
  leg_id: z.string().nullable(),
  stop_loss: z
    .object({
      type: z.string(),
      value: z.number(),
      trail_by: z.number().nullable(),
    })
    .nullable(),
  take_profit: z
    .object({
      type: z.string(),
      value: z.number(),
    })
    .nullable(),
});

export const signalSchema = z.object({
  signal_id: z.string(),
  strategy_id: z.string(),
  emitted_at: z.string(),
  reason: z.string(),
  target_state: z.object({ legs: z.array(signalLegSchema) }),
  indicator_snapshot: z.record(z.unknown()),
});
export const signalListSchema = z.array(signalSchema);

export const riskViolationSchema = z.object({
  id: z.number(),
  rule_id: z.string().nullable(),
  symbol: z.string().nullable(),
  side: z.enum(['buy', 'sell']).nullable(),
  quantity: z.number().nullable(),
  reason: z.string(),
  at: z.string(),
});
export const riskViolationListSchema = z.array(riskViolationSchema);

export const reconciliationEventSchema = z.object({
  id: z.string(),
  type: z.enum(['MISSING_ON_BROKER', 'UNKNOWN_ON_BROKER', 'QTY_MISMATCH']),
  symbol: z.string(),
  db_qty: z.number(),
  broker_qty: z.number(),
  resolved: z.boolean(),
  at: z.string(),
});
export const reconciliationEventListSchema = z.array(reconciliationEventSchema);
